export interface ExtractionStartResponse {
  status: string;
  record_id: string;
  message: string;
  metadata: {
    existing_result?: boolean;
    new_processing?: boolean;
    record_status?: string;
    ai_doctype?: string;
    tenant_doctype?: string;
    [key: string]: any;
  };
}

export interface ExtractionStatusResponse {
  record_status:
    | 'pending'
    | 'text_extracted'
    | 'ocr_complete'
    | 'classified'
    | 'generating extraction prompt'
    | 'extract prompt successful'
    | 'extracted'
    | 'failed'
    | 'extraction failed'
    | 'extraction prompt failed';
  ai_doctype?: string;
  tenant_doctype?: string;
  created_at?: string;
  updated_at?: string;
  classification_result?: {
    document_type: string;
    confidence: number;
  };
  extraction_result?: Record<string, any>;
}

export class DocumentAnalysisSdk {
  private baseURL: string;

  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }
    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }
    this.baseURL = baseURL;
  }

  /**
   * Generic fetch wrapper to handle responses
   * @param endpoint The API endpoint to call
   * @param options Optional fetch options
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          errorMessage: errorData.errorMessage || errorData.message
        })
      );
    }
    return response;
  }

  /**
   * Starts the extraction process for a given file
   * @param documentId The document identifier
   * @param tenantId The tenant identifier
   * @param accessToken The access token for authorization
   * @param documentTypeName Optional documentType name parameter for no-classify extraction
   * @param force
   */
  async startExtraction(
    documentId: string,
    tenantId: string,
    accessToken: string,
    force: boolean = false,
    documentTypeName?: string
  ): Promise<ExtractionStartResponse> {
    let action = 'classifyextract';
    if (documentTypeName) {
      action = 'extract';
    }

    let endpoint = `/s3/triggerconverseasync?tenantId=${encodeURIComponent(tenantId)}&document_id=${encodeURIComponent(documentId)}&action=${action}`;

    if (documentTypeName) {
      endpoint += `&content_doctype=${encodeURIComponent(documentTypeName)}`;
      force = true;
    }

    if (force) {
      endpoint += '&force=true';
    }

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Gets the extraction status for a given record ID
   * @param recordId The record identifier
   * @param tenantId The tenant identifier
   * @param accessToken The access token for authorization
   */
  async getExtractionStatus(
    recordId: string,
    tenantId: string,
    accessToken: string
  ): Promise<ExtractionStatusResponse> {
    const endpoint = `/s3/record?id=${encodeURIComponent(recordId)}&tenantId=${encodeURIComponent(tenantId)}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response.json();
  }
}
